
import React, { useState, useEffect } from 'react';
import FormModal from './FormModal';
import { FaTrashAlt } from 'react-icons/fa';
import api from '../../services/api';

const Quotes = () => {
  const [quotes, setQuotes] = useState([]);
  const [filteredQuotes, setFilteredQuotes] = useState([]);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filter, setFilter] = useState({ date: '', phone: '' });
  const [hoveredRow, setHoveredRow] = useState(null);

  const fetchQuotes = async () => {
    try {
      const data = await api.get('quotes');
      if (Array.isArray(data)) {
        setQuotes(data);
        setFilteredQuotes(data);
      } else {
        console.error('Error: API did not return an array of quotes.', data);
        setQuotes([]);
        setFilteredQuotes([]);
      }
    } catch (error) {
      console.error('Error fetching quotes:', error);
      setQuotes([]);
      setFilteredQuotes([]);
    }
  };

  useEffect(() => {
    fetchQuotes();
  }, []);

  useEffect(() => {
    let filtered = quotes;
    if (filter.date) {
      filtered = filtered.filter(quote => new Date(quote.createdAt).toLocaleDateString() === new Date(filter.date).toLocaleDateString());
    }
    if (filter.phone) {
      filtered = filtered.filter(quote => quote.phone.includes(filter.phone));
    }
    setFilteredQuotes(filtered);
  }, [filter, quotes]);

  const handleView = (quote) => {
    setSelectedQuote(quote);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this quote?')) {
      try {
        await api.delete(`quotes/${id}`);
        fetchQuotes();
      } catch (error) {
        console.error('Error deleting quote:', error);
      }
    }
  };

  const handleClearAll = async () => {
    if (window.confirm('Are you sure you want to delete all quotes?')) {
      try {
        await api.delete('quotes');
        fetchQuotes();
      } catch (error) {
        console.error('Error clearing all quotes:', error);
      }
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilter(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="p-4 bg-gray-50 min-h-screen">
      <h1 className="text-3xl font-extrabold text-gray-800 mb-6">Quotes Management</h1>
      <div className="flex justify-end mb-6 space-x-4">
        <button
          onClick={async () => {
            try {
              const response = await api.download('quotes/download');
              const blob = await response.blob();
              const url = window.URL.createObjectURL(new Blob([blob]));
              const link = document.createElement('a');
              link.href = url;
              link.setAttribute('download', 'quotes.csv');
              document.body.appendChild(link);
              link.click();
              link.parentNode.removeChild(link);
            } catch (error) {
              console.error('Error downloading quotes:', error);
            }
          }}
          className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-md shadow-md transition duration-300"
        >
          Download CSV
        </button>
        <button
          onClick={handleClearAll}
          className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-md shadow-md transition duration-300"
        >
          Clear All Quotes
        </button>
      </div>
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 space-y-4 md:space-y-0">
        <div className="flex space-x-2">
          <input
            type="date"
            name="date"
            value={filter.date}
            onChange={handleFilterChange}
            className="p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 shadow-sm"
          />
          <input
            type="text"
            name="phone"
            placeholder="Filter by phone..."
            value={filter.phone}
            onChange={handleFilterChange}
            className="p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 shadow-sm"
          />
        </div>
      </div>

      <div className="bg-white shadow-lg rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Country</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredQuotes.map((quote) => (
                <tr
                  key={quote.id}
                  className="hover:bg-gray-50 transition duration-150 ease-in-out"
                  onMouseEnter={() => setHoveredRow(quote.id)}
                  onMouseLeave={() => setHoveredRow(null)}
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{quote.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{quote.email}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{quote.phone}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{quote.country}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleView(quote)}
                      className="text-blue-600 hover:text-blue-900 mr-4"
                    >
                      View
                    </button>
                    {hoveredRow === quote.id && (
                      <button
                        onClick={() => handleDelete(quote.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <FaTrashAlt className="inline-block mr-1" /> Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {selectedQuote && <FormModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} formData={selectedQuote} />}
    </div>
  );
};

export default Quotes;

